import { useCallback, useEffect, useRef, useState } from 'react'

export type CollapsedDropdownTarget = 'attention' | 'start' | 'duration' | 'deadline' | 'tags' | 'reward' | 'recurrence'

export interface CollapsedDropdowns {
  attentionOpen: boolean
  setAttentionOpen: (open: boolean) => void
  startOpen: boolean
  setStartOpen: (open: boolean) => void
  durationOpen: boolean
  setDurationOpen: (open: boolean) => void
  deadlineOpen: boolean
  setDeadlineOpen: (open: boolean) => void
  tagsOpen: boolean
  setTagsOpen: (open: boolean) => void
  rewardOpen: boolean
  setRewardOpen: (open: boolean) => void
  recurrenceOpen: boolean
  setRecurrenceOpen: (open: boolean) => void
  /** Which tag pill owns the open tags dropdown ('__overflow__' for the +N pill). */
  activeTagId: string | null
  setActiveTagId: (id: string | null) => void
  anyOpen: boolean
  /**
   * Hovering a sibling badge while ANY dropdown is open moves the open
   * dropdown to that badge (fluent badge-to-badge browsing). No-op while
   * everything is closed.
   */
  switchTo: (target: CollapsedDropdownTarget, tagId?: string) => void
}

/**
 * Open/close state of the seven collapsed badge dropdowns (attention,
 * start, duration, deadline, tags, reward, recurrence) plus:
 *
 * - reset (except tags) when the entry expands — the collapsed badges
 *   unmount, their dropdowns must not reopen on collapse
 * - focus restore to the task-list wrapper after the last dropdown
 *   closes, so keyboard navigation continues where it left off
 */
export function useCollapsedDropdowns(
  isExpanded: boolean,
  entryRef: React.RefObject<HTMLDivElement | null>,
): CollapsedDropdowns {
  const [attentionOpen, setAttentionOpen] = useState(false)
  const [startOpen, setStartOpen] = useState(false)
  const [durationOpen, setDurationOpen] = useState(false)
  const [deadlineOpen, setDeadlineOpen] = useState(false)
  const [tagsOpen, setTagsOpen] = useState(false)
  const [rewardOpen, setRewardOpen] = useState(false)
  const [recurrenceOpen, setRecurrenceOpen] = useState(false)
  const [activeTagId, setActiveTagId] = useState<string | null>(null)

  const anyOpen = attentionOpen || startOpen || durationOpen || deadlineOpen || tagsOpen || rewardOpen || recurrenceOpen
  const anyOpenRef = useRef(false)
  anyOpenRef.current = anyOpen

  const switchTo = useCallback((target: CollapsedDropdownTarget, tagId?: string) => {
    if (!anyOpenRef.current) return
    setAttentionOpen(target === 'attention')
    setStartOpen(target === 'start')
    setDurationOpen(target === 'duration')
    setDeadlineOpen(target === 'deadline')
    setTagsOpen(target === 'tags')
    setRewardOpen(target === 'reward')
    setRecurrenceOpen(target === 'recurrence')
    setActiveTagId(target === 'tags' ? (tagId ?? null) : null)
  }, [])

  // ── Reset badge dropdowns when the entry expands ──
  const prevExpandedRef = useRef(isExpanded)
  if (prevExpandedRef.current !== isExpanded) {
    prevExpandedRef.current = isExpanded
    if (isExpanded) {
      setAttentionOpen(false)
      setStartOpen(false)
      setDurationOpen(false)
      setDeadlineOpen(false)
      setRewardOpen(false)
      setRecurrenceOpen(false)
    }
  }

  // ── Restore focus to the task list wrapper after closing a dropdown ──
  const prevAnyOpenRef = useRef(false)
  useEffect(() => {
    if (prevAnyOpenRef.current && !anyOpen && !isExpanded) {
      const wrapper = entryRef.current?.closest('.project-page__task-list-wrap') as HTMLElement | null
      if (wrapper) wrapper.focus()
    }
    prevAnyOpenRef.current = anyOpen
  }, [anyOpen, isExpanded, entryRef])

  return {
    attentionOpen, setAttentionOpen,
    startOpen, setStartOpen,
    durationOpen, setDurationOpen,
    deadlineOpen, setDeadlineOpen,
    tagsOpen, setTagsOpen,
    rewardOpen, setRewardOpen,
    recurrenceOpen, setRecurrenceOpen,
    activeTagId, setActiveTagId,
    anyOpen,
    switchTo,
  }
}
